// ===========================================
// COMPONENTE: ImpuestosSelector
// ===========================================
// Multi-select de impuestos con checkboxes

import * as React from 'react'
import { Check, ChevronDown, ChevronRight, Plus, X, Settings, Lock, AlertTriangle, Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Input } from '@/components/ui/input'
import { ImpuestoBadge } from '@/components/ui/impuesto-badge'
import type { CatalogoImpuesto, ImpuestoLiquidar, CategoriaImpuestoCatalogo } from '@/types/impuestos'
import { CATEGORIAS_IMPUESTO_CATALOGO } from '@/types/impuestos'

// Cuántos badges mostrar inline antes de colapsar
const MAX_BADGES_INLINE = 3

// ORR-100 PR3 F2.3: agrupación COMARB.
//   SIFERE es el "default visible" porque aplica a la mayoría de contribuyentes IIBB.
//   SIRCAR/SIRCREB/SIRTAC/SIRCUPA aplican a casos especiales y se ocultan detrás
//   de un toggle "Mostrar más" para reducir carga cognitiva.
const COMARB_DEFAULT_CODE = 'sifere_cm'
const COMARB_EXTRA_CODES = ['sircar', 'sircreb', 'sirtac', 'sircupa'] as const
const COMARB_ALL_CODES = [COMARB_DEFAULT_CODE, ...COMARB_EXTRA_CODES]

const COMARB_TOOLTIPS: Record<string, string> = {
  sifere_cm: 'Liquidación mensual + CM05 anual. Aplica a contribuyentes inscriptos en Convenio Multilateral.',
  sircar: 'Sistema de Recaudación y Control para Agentes de Retención/Percepción.',
  sircreb: 'Retenciones sobre acreditaciones bancarias.',
  sirtac: 'Retenciones sobre tarjetas de crédito/compra.',
  sircupa: 'Retenciones sobre operaciones con billeteras virtuales.',
}

interface ImpuestosSelectorProps {
  catalogo: CatalogoImpuesto[]
  value: ImpuestoLiquidar[]
  onChange: (impuestos: ImpuestoLiquidar[]) => void
  disabled?: boolean
  onCrearNuevo?: (nombre: string) => void
  onGestionar?: () => void
}

export function ImpuestosSelector({
  catalogo,
  value = [],
  onChange,
  disabled = false,
  onCrearNuevo,
  onGestionar,
}: ImpuestosSelectorProps) {
  const [open, setOpen] = React.useState(false)
  const [busqueda, setBusqueda] = React.useState('')
  const [comarbExpanded, setComarbExpanded] = React.useState(false)
  const scrollRef = React.useRef<HTMLDivElement>(null)

  // Handler manual para wheel events (fix para Radix Popover)
  const handleWheel = React.useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    const el = scrollRef.current
    if (!el) return

    e.stopPropagation()
    el.scrollTop += e.deltaY
  }, [])

  // IDs seleccionados para lookup rapido
  const seleccionados = React.useMemo(
    () => new Set(value.map((v) => v.impuesto_id)),
    [value]
  )

  // Filtrar catalogo
  const catalogoFiltrado = React.useMemo(() => {
    if (!busqueda) return catalogo
    const query = busqueda.toLowerCase()
    return catalogo.filter(
      (imp) =>
        imp.nombre.toLowerCase().includes(query) ||
        imp.nombre_corto?.toLowerCase().includes(query) ||
        imp.codigo.toLowerCase().includes(query)
    )
  }, [catalogo, busqueda])

  // Agrupar por categoria
  const porCategoria = React.useMemo(() => {
    const grupos: Record<CategoriaImpuestoCatalogo, CatalogoImpuesto[]> = {
      iva: [],
      ganancias: [],
      iibb: [],
      patrimonial: [],
      bienes: [],
      laboral: [],
      autonomos: [],
      suss: [],
      retenciones: [],
      municipal: [],
      monotributo: [],
      otros: [],
    }
    catalogoFiltrado.forEach((imp) => {
      const cat = imp.categoria as CategoriaImpuestoCatalogo
      if (grupos[cat]) {
        grupos[cat].push(imp)
      } else {
        grupos.otros.push(imp)
      }
    })
    return grupos
  }, [catalogoFiltrado])

  // Toggle seleccion
  const handleToggle = (impuesto: CatalogoImpuesto) => {
    if (seleccionados.has(impuesto.id)) {
      // Quitar
      onChange(value.filter((v) => v.impuesto_id !== impuesto.id))
    } else {
      // Agregar
      onChange([
        ...value,
        {
          impuesto_id: impuesto.id,
          codigo: impuesto.codigo,
        },
      ])
    }
  }

  // Quitar impuesto
  const handleQuitar = (impuestoId: string) => {
    onChange(value.filter((v) => v.impuesto_id !== impuestoId))
  }

  // Obtener datos completos de un impuesto seleccionado
  const obtenerDatos = (impuestoId: string) => {
    return catalogo.find((c) => c.id === impuestoId)
  }

  // Items huérfanos: seleccionados pero no están en catálogo (impuesto dado de baja)
  const huerfanos = React.useMemo(
    () => value.filter((v) => !catalogo.some((c) => c.id === v.impuesto_id)),
    [value, catalogo]
  )

  // Badges para mostrar inline (máximo MAX_BADGES_INLINE)
  const badgesInline = React.useMemo(() => {
    return value.slice(0, MAX_BADGES_INLINE).map((imp) => {
      const datos = obtenerDatos(imp.impuesto_id)
      if (datos) return { id: imp.impuesto_id, nombre: datos.nombre_corto || datos.nombre, color: datos.color, inactivo: false }
      return { id: imp.impuesto_id, nombre: imp.codigo || 'Dado de baja', color: 'gray' as const, inactivo: true }
    })
  }, [value, obtenerDatos])

  const restantes = value.length - MAX_BADGES_INLINE

  return (
    <div className="space-y-2">
      {/* Selector con badges inline */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn(
              'w-full justify-between font-normal h-auto min-h-10 px-3 py-2',
              value.length === 0 && 'text-muted-foreground'
            )}
          >
            <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
              {value.length === 0 ? (
                <>
                  <Plus className="h-4 w-4 shrink-0" />
                  <span>Seleccionar impuestos...</span>
                </>
              ) : (
                <>
                  {badgesInline.map((item) => (
                    <ImpuestoBadge
                      key={item.id}
                      nombre={item.nombre}
                      color={item.color}
                      size="sm"
                    />
                  ))}
                  {restantes > 0 && (
                    <span className="text-xs text-muted-foreground">
                      +{restantes} más
                    </span>
                  )}
                </>
              )}
            </div>
            <ChevronDown className={cn(
              'h-4 w-4 shrink-0 opacity-50 transition-transform',
              open && 'rotate-180'
            )} />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          {/* Busqueda */}
          <div className="p-2 border-b">
            <Input
              placeholder="Buscar impuesto..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="h-8"
            />
          </div>

          {/* Lista por categorias */}
          <div
            ref={scrollRef}
            onWheel={handleWheel}
            className="max-h-72 overflow-y-auto overscroll-contain"
          >
            <div className="p-2 space-y-4">
              {(Object.entries(porCategoria) as [CategoriaImpuestoCatalogo, CatalogoImpuesto[]][])
                .filter(([, impuestos]) => impuestos.length > 0)
                .map(([categoria, impuestos]) => {
                  // F2.3: agrupar COMARB en IIBB.
                  // Separamos los 5 codigos COMARB del resto de IIBB.
                  // SIFERE va default visible; SIRCAR/SIRCREB/SIRTAC/SIRCUPA detrás de toggle.
                  const isIibb = categoria === 'iibb'
                  const comarbItems = isIibb
                    ? impuestos.filter((i) => COMARB_ALL_CODES.includes(i.codigo))
                    : []
                  const otrosItems = isIibb
                    ? impuestos.filter((i) => !COMARB_ALL_CODES.includes(i.codigo))
                    : impuestos
                  // Si hay búsqueda activa y matchea algún COMARB extra, lo mostramos sin requerir toggle.
                  const sifere = comarbItems.find((i) => i.codigo === COMARB_DEFAULT_CODE)
                  const extras = comarbItems.filter((i) => COMARB_EXTRA_CODES.includes(i.codigo as typeof COMARB_EXTRA_CODES[number]))
                  const expandExtras = comarbExpanded || busqueda.length > 0

                  return (
                    <div key={categoria}>
                      <p className="text-xs font-medium text-muted-foreground px-2 mb-1">
                        {CATEGORIAS_IMPUESTO_CATALOGO[categoria]}
                      </p>
                      <div className="space-y-0.5">
                        {otrosItems.map((impuesto) => (
                          <ImpuestoRow
                            key={impuesto.id}
                            impuesto={impuesto}
                            selected={seleccionados.has(impuesto.id)}
                            onToggle={() => handleToggle(impuesto)}
                          />
                        ))}
                        {isIibb && comarbItems.length > 0 && (
                          <div className="mt-2 ml-2 border-l-2 border-muted pl-2 py-1 space-y-0.5">
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide px-2 mb-1">
                              Convenio Multilateral
                            </p>
                            {sifere && (
                              <ImpuestoRow
                                impuesto={sifere}
                                selected={seleccionados.has(sifere.id)}
                                onToggle={() => handleToggle(sifere)}
                                tooltip={COMARB_TOOLTIPS[sifere.codigo]}
                              />
                            )}
                            {extras.length > 0 && (
                              <>
                                {expandExtras
                                  ? extras.map((impuesto) => (
                                      <ImpuestoRow
                                        key={impuesto.id}
                                        impuesto={impuesto}
                                        selected={seleccionados.has(impuesto.id)}
                                        onToggle={() => handleToggle(impuesto)}
                                        tooltip={COMARB_TOOLTIPS[impuesto.codigo]}
                                      />
                                    ))
                                  : null}
                                {busqueda.length === 0 && (
                                  <button
                                    type="button"
                                    onClick={() => setComarbExpanded((v) => !v)}
                                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground px-2 py-1 transition-colors"
                                  >
                                    <ChevronRight className={cn('h-3 w-3 transition-transform', comarbExpanded && 'rotate-90')} />
                                    {comarbExpanded ? 'Ocultar' : `Mostrar ${extras.length} más`}
                                    {!comarbExpanded && (
                                      <span className="text-muted-foreground/60"> (SIRCAR, SIRCREB, SIRTAC, SIRCUPA)</span>
                                    )}
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}

              {catalogoFiltrado.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-sm text-muted-foreground mb-2">
                    No se encontraron impuestos
                  </p>
                  {onCrearNuevo && busqueda && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        onCrearNuevo(busqueda)
                        setBusqueda('')
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Crear "{busqueda}"
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Seleccionados dentro del popover */}
          {value.length > 0 && (
            <div className="p-2 border-t bg-muted/30">
              <p className="text-xs font-medium text-muted-foreground mb-2">
                Seleccionados ({value.length})
              </p>
              <div className="flex flex-wrap gap-1.5">
                {value.map((imp) => {
                  const datos = obtenerDatos(imp.impuesto_id)
                  const nombre = datos
                    ? (datos.nombre_corto || datos.nombre)
                    : (imp.codigo || 'Dado de baja')
                  const color = datos?.color || 'gray'
                  return (
                    <div
                      key={imp.impuesto_id}
                      className={cn(
                        'inline-flex items-center gap-1 rounded-md pr-1',
                        datos ? 'bg-background' : 'bg-destructive/10'
                      )}
                    >
                      {!datos && <AlertTriangle className="h-3 w-3 text-destructive ml-1" />}
                      <ImpuestoBadge
                        nombre={nombre}
                        color={color}
                        size="sm"
                      />
                      <button
                        type="button"
                        onClick={() => handleQuitar(imp.impuesto_id)}
                        className="rounded-full p-0.5 hover:bg-muted transition-colors"
                      >
                        <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Huérfanos: impuestos dados de baja que aún están asignados */}
          {huerfanos.length > 0 && (
            <div className="p-2 border-t bg-destructive/5">
              <p className="text-xs font-medium text-destructive flex items-center gap-1 mb-2">
                <AlertTriangle className="h-3 w-3" />
                Dados de baja ({huerfanos.length})
              </p>
              <div className="space-y-0.5">
                {huerfanos.map((imp) => (
                  <div
                    key={imp.impuesto_id}
                    className="flex items-center justify-between px-2 py-1.5 rounded-md bg-destructive/10"
                  >
                    <span className="text-sm text-muted-foreground line-through">
                      {imp.codigo || imp.impuesto_id.slice(0, 8)}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleQuitar(imp.impuesto_id)}
                      className="text-xs text-destructive hover:text-destructive/80 font-medium"
                    >
                      Quitar
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer con botones */}
          <div className="p-2 border-t flex items-center gap-2">
            {onCrearNuevo && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={() => {
                  onCrearNuevo('')
                }}
              >
                <Plus className="h-4 w-4 mr-1" />
                Nuevo
              </Button>
            )}
            {onGestionar && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={() => {
                  setOpen(false)
                  onGestionar()
                }}
              >
                <Settings className="h-4 w-4 mr-1" />
                Gestionar
              </Button>
            )}
            <Button
              type="button"
              variant="default"
              size="sm"
              className="ml-auto"
              onClick={() => setOpen(false)}
            >
              <Check className="h-4 w-4 mr-1" />
              Listo
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Helper text */}
      {value.length === 0 && (
        <p className="text-xs text-muted-foreground">
          Selecciona los impuestos que liquida este cliente
        </p>
      )}
    </div>
  )
}

interface ImpuestoRowProps {
  impuesto: CatalogoImpuesto
  selected: boolean
  onToggle: () => void
  tooltip?: string
}

function ImpuestoRow({ impuesto, selected, onToggle, tooltip }: ImpuestoRowProps) {
  return (
    <label
      className={cn(
        'flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer',
        'hover:bg-muted transition-colors',
        selected && 'bg-muted'
      )}
    >
      <Checkbox checked={selected} onCheckedChange={onToggle} />
      <ImpuestoBadge nombre={impuesto.nombre_corto || impuesto.nombre} color={impuesto.color} />
      <span className="text-sm truncate flex-1">{impuesto.nombre}</span>
      {tooltip && (
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="shrink-0 text-muted-foreground hover:text-foreground" onClick={(e) => e.preventDefault()}>
                <Info className="h-3 w-3" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-xs text-xs">
              {tooltip}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {impuesto.is_global && (
        <Lock className="h-3 w-3 text-muted-foreground shrink-0" />
      )}
    </label>
  )
}
